import React, { useEffect, useState } from "react";
import { usePageMetadata } from "../context/PageMetadataContext";
import { Table, Card, Row, Col, Badge, Spinner } from "react-bootstrap";
import { useLanguage } from "../context/LanguageContext";

// Define types for the testnet API data
interface CountryStat {
  country: string;
  total_nodes: number;
  active_nodes: number;
}

interface CityStat {
  city: string;
  country: string;
  total_nodes: number;
  active_nodes: number;
}

interface StatusBreakdown {
  active: number;
  inactive: number;
}

interface TypeBreakdown {
  peer: number;
}

interface TestnetNodeData {
  total_nodes: number;
  active_nodes: number;
  inactive_nodes: number;
  last_updated: string;
  status_breakdown: StatusBreakdown;
  type_breakdown: TypeBreakdown;
  country_stats: CountryStat[];
  city_stats: CityStat[];
}

const NodesTestnet: React.FC = () => {
  const [nodeData, setNodeData] = useState<TestnetNodeData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const { t, language } = useLanguage();

  useEffect(() => {
    setHeading(t('nodes_testnet.heading'));
    setTitle(t('nodes_testnet.title'));
    setDescription(t('nodes_testnet.description'));
  }, [setHeading, setTitle, setDescription, t, language]);
  
  useEffect(() => {
    fetchTestnetNodes();
  }, []);

  const fetchTestnetNodes = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://nodes.piscan.io/api/statistics/nodes');
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const data = await response.json();
      setNodeData(data);
      setLoading(false);
    } catch (err) {
      setError(`Failed to fetch testnet node data: ${err instanceof Error ? err.message : String(err)}`);
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString(language);
  };

  
  const calculateActivePercentage = (active: number, total: number): number => {
    return total > 0 ? (active / total) * 100 : 0;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!nodeData) {
    return <div className="alert alert-warning">No testnet node data available</div>;
  }

  return (
    <div className="nodes-testnet-page">
      {/* Summary Information */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <h5 className="card-title">{t('nodes_testnet.summary_title')}</h5>
              <p className="card-text text-muted">
                {t('nodes_testnet.summary_description')}
              </p>
              <div className="row text-center">
                <div className="col-md-4">
                  <div className="border-end">
                    <h3 className="text-primary">{formatNumber(nodeData.total_nodes)}</h3>
                    <p className="text-muted mb-0">{t('nodes_testnet.total_peers')}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="border-end">
                    <h3 className="text-success">{formatNumber(nodeData.active_nodes)}</h3>
                    <p className="text-muted mb-0">{t('nodes_testnet.active_peers')}</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <h3 className="text-info">
                    {calculateActivePercentage(nodeData.active_nodes, nodeData.total_nodes).toFixed(1)}%
                  </h3>
                  <p className="text-muted mb-0">{t('nodes_testnet.activity_rate')}</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Country and City Statistics Tables */}
      <Row className="mb-4">
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h4 className="card-title mb-0">
                {t('nodes_testnet.country_statistics')} (Top 10)
              </h4>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('nodes_testnet.country')}</th>
                      <th className="text-end">{t('nodes_testnet.total_nodes')}</th>
                      <th className="text-end">{t('nodes_testnet.active_nodes')}</th>
                      <th className="text-end">{t('nodes_testnet.active_rate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeData.country_stats
                      .sort((a, b) => b.active_nodes - a.active_nodes) // Sort by active nodes descending
                      .slice(0, 10) // Top 10 countries
                      .map((country, index) => (
                        <tr key={country.country}>
                          <td>
                            <span className="fw-medium">{country.country}</span>
                          </td>
                          <td className="text-end">
                            <Badge bg="secondary" className="px-2 py-1">
                              {formatNumber(country.total_nodes)}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Badge bg="success" className="px-2 py-1">
                              {formatNumber(country.active_nodes)}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <span className="small text-muted">
                              {calculateActivePercentage(country.active_nodes, country.total_nodes).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={6} className="mb-3">
          <Card>
            <Card.Header>
              <h4 className="card-title mb-0">
                {t('nodes_testnet.city_statistics')} (Top 10)
              </h4>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>{t('nodes_testnet.city')}</th>
                      <th className="text-end">{t('nodes_testnet.total_nodes')}</th>
                      <th className="text-end">{t('nodes_testnet.active_nodes')}</th>
                      <th className="text-end">{t('nodes_testnet.active_rate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nodeData.city_stats
                      .sort((a, b) => b.active_nodes - a.active_nodes) // Sort by active nodes descending
                      .slice(0, 10) // Top 10 cities
                      .map((city, index) => (
                        <tr key={`${city.city}-${city.country}`}>
                          <td>
                            <div>
                              <span className="fw-medium">{city.city}</span>
                              {/* <br />
                              <small className="text-muted">{city.country}</small> */}
                            </div>
                          </td>
                          <td className="text-end">
                            <Badge bg="secondary" className="px-2 py-1">
                              {formatNumber(city.total_nodes)}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Badge bg="info" className="px-2 py-1">
                              {formatNumber(city.active_nodes)}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <span className="small text-muted">
                              {calculateActivePercentage(city.active_nodes, city.total_nodes).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      
    </div>
  );
};

export default NodesTestnet;